/**
 * Zuordnung — „Was ist das?" NACH dem Scannen (seit Scanner 0.7, 10/2026).
 *
 * Früher stand diese Seite VOR der Kamera. Wie bei Lexware Scan wird jetzt
 * zuerst gescannt und geprüft, danach zugeordnet; „Sichern" lädt hoch.
 * Kategorie + Projektzuordnung auf EINEM Screen — kein Extra-Schritt:
 *   Vier Kategorien: Rechnung · Bewirtungsbeleg · Lieferschein · Privat.
 *   Darunter direkt die Projektauswahl (kein Projekt / Heute-Chips / Suche).
 *   Vorausgewählt ist die Baustelle, auf der man heute selbst eingeteilt ist.
 *
 * Regeln:
 *   Rechnung        → genau ein Projekt ODER „kein Projekt" (= Betriebsausgabe).
 *   Bewirtungsbeleg → genau ein Projekt (Pflicht, danach nicht mehr änderbar).
 *   Lieferschein    → ein oder mehrere Projekte.
 * Helferrechnungen laufen NICHT mehr über den Scan (kommen per Mail/Team-App).
 *
 * Der CaptureContext, den CapturePage zum Hochladen braucht, bleibt unverändert.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, istAbgeschlossen, type AusgabeTyp, type ProjektShort } from "../lib/api";
import "./ContextSelectPage.css";

export type CaptureContext = {
  ausgabe_typ: AusgabeTyp | "";
  projekt_id: string;
  projekt_title: string;
  is_bewirtung: boolean;
  helfer_id: string;
  helfer_name: string;
  /** Belegtyp; für Lieferscheine 'lieferschein' (sonst leer → KI/Default). */
  beleg_typ?: string;
  /** Lieferscheine: mehrere vorausgewählte Projekte. */
  projekt_ids?: string[];
  projekt_titles?: string[];
  /** F1: privates Dokument — läuft in die Privat-Pipeline, nicht in beleg. */
  privat?: boolean;
};

// ───────────────────────── Icons ─────────────────────────
type IconName = "back" | "receipt" | "truck" | "coffee" | "building" | "search" | "camera" | "check" | "x" | "ban" | "lock";

function SIcon({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    back: <path d="M15 5l-7 7 7 7" />,
    receipt: (
      <>
        <path d="M6 3.2h12v17.6l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4-2 1.4z" />
        <path d="M9 8h6M9 11.5h6M9 15h4" />
      </>
    ),
    truck: (
      <>
        <path d="M3 6.5h10v9H3z" />
        <path d="M13 9.5h4l3 3v3h-7z" />
        <circle cx="7" cy="17.5" r="1.8" />
        <circle cx="17" cy="17.5" r="1.8" />
      </>
    ),
    coffee: (
      <>
        <path d="M4 8.5h13v4a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z" />
        <path d="M17 9.5h1.8a2.2 2.2 0 0 1 0 4.4H17" />
        <path d="M7 3.2v2M10.5 3.2v2M14 3.2v2" />
      </>
    ),
    building: (
      <>
        <rect x="6" y="3.5" width="12" height="17" rx="1.6" />
        <path d="M9.5 7.5h2M12.5 7.5h2M9.5 11h2M12.5 11h2M9.5 14.5h2M12.5 14.5h2" />
        <path d="M10 20.5v-3h4v3" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M16.5 16.5L21 21" />
      </>
    ),
    camera: (
      <>
        <path d="M4 8.5h3l1.6-2.4h6.8L17 8.5h3a1 1 0 0 1 1 1V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1z" />
        <circle cx="12" cy="13" r="3.6" />
      </>
    ),
    check: <path d="M5 12.5 10 17 19 7" />,
    x: <path d="M6 6l12 12M18 6L6 18" />,
    ban: (
      <>
        <circle cx="12" cy="12" r="8.2" />
        <path d="M6.3 6.3l11.4 11.4" />
      </>
    ),
    lock: (
      <>
        <rect x="5" y="10.5" width="14" height="10" rx="2" />
        <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7}
         strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

// ───────────────────────── Kategorien ─────────────────────────
export type Cat = "rechnung" | "bewirtung" | "lieferschein" | "privat";
/** Die Auswahl auf dieser Seite — CapturePage merkt sie sich, damit nach einem
 *  fehlgeschlagenen Hochladen nicht wieder bei „Rechnung" begonnen wird (03.11.2026). */
export type Wahl = { cat: Cat; single: string | null; multi: string[] };
const NO_PROJECT = "__none__";

const KATEGORIEN: Record<Cat, { label: string; icon: IconName; multi: boolean; erlaubtKeinProjekt: boolean; hint: string }> = {
  rechnung: {
    label: "Rechnung",
    icon: "receipt",
    multi: false,
    erlaubtKeinProjekt: true,
    hint: "Material, Sprit, Werkzeug — einem Projekt zuordnen oder ohne Projekt als Betriebskosten.",
  },
  bewirtung: {
    label: "Bewirtungsbeleg",
    icon: "coffee",
    multi: false,
    erlaubtKeinProjekt: false,
    hint: "Immer mit Projektbezug. Die Zuordnung ist danach fest und nicht mehr änderbar.",
  },
  lieferschein: {
    label: "Lieferschein",
    icon: "truck",
    multi: true,
    erlaubtKeinProjekt: false,
    hint: "Lieferung für ein oder mehrere Projekte.",
  },
  privat: {
    label: "Privat",
    icon: "lock",
    multi: false,
    erlaubtKeinProjekt: false,
    hint: "Privates Dokument — ohne Projekt, wird getrennt von den Firmenbelegen abgelegt.",
  },
};

const IST_PRIVAT = (c: Cat) => c === "privat";

const HEUTE_COUNT = 3;

type ZuordnungProps = {
  anfangsKat?: Cat;
  /** Die Auswahl vom letzten Versuch (nach einem Fehler beim Hochladen). */
  vorher?: Wahl | null;
  /** Vorschau der ersten Seite (oder null bei einem gewählten PDF) */
  vorschauUrl: string | null;
  /** z. B. „2 Seiten" oder „PDF" */
  umfang: string;
  onZurueck: () => void;
  onSichern: (ctx: CaptureContext, wahl: Wahl) => void;
};

export default function Zuordnung({ anfangsKat, vorher, vorschauUrl, umfang, onZurueck, onSichern }: ZuordnungProps) {

  const heuteQ = useQuery({
    queryKey: ["projekte-heute"],
    queryFn: () => api.listProjekte({ today: true }),
    staleTime: 5 * 60 * 1000,
  });
  // Heißt noch „aktive", enthält seit 19.10.2026 aber auch abgeschlossene Projekte.
  const aktiveQ = useQuery({
    queryKey: ["projekte-zuordnung"],
    queryFn: () => api.listProjekte({}),
    staleTime: 5 * 60 * 1000,
  });

  const [cat, setCat] = useState<Cat>(vorher?.cat ?? anfangsKat ?? "rechnung");
  const [q, setQ] = useState("");
  // Einzelauswahl (Rechnung/Bewirtung): Projekt-ID oder NO_PROJECT oder null.
  const [selSingle, setSelSingle] = useState<string | null>(vorher?.single ?? null);
  // Mehrfachauswahl (Lieferschein): Projekt-IDs.
  const [selMulti, setSelMulti] = useState<string[]>(vorher?.multi ?? []);

  const k = KATEGORIEN[cat];

  // Die eigene Baustelle von heute vorauswählen (nur solange nichts gewählt ist).
  const meinHeute = (heuteQ.data ?? []).find((p) => p.mein);
  function vorauswahl(c: Cat) {
    if (!meinHeute || c === "privat") return;
    if (KATEGORIEN[c].multi) setSelMulti([meinHeute.id]);
    else setSelSingle(meinHeute.id);
  }
  useEffect(() => {
    if (selSingle === null && selMulti.length === 0) vorauswahl(cat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meinHeute?.id]);

  function chooseCat(next: Cat) {
    setCat(next);
    setQ("");
    setSelSingle(null);
    setSelMulti([]);
    vorauswahl(next);
  }

  const projektById = useMemo(() => {
    const m = new Map<string, ProjektShort>();
    [...(aktiveQ.data ?? []), ...(heuteQ.data ?? [])].forEach((p) => m.set(p.id, p));
    return m;
  }, [aktiveQ.data, heuteQ.data]);

  // ── Bereitschaft + Zusammenfassung ──
  let ready = false;
  let summary: React.ReactNode;
  if (IST_PRIVAT(cat)) {
    ready = true; // privat braucht keine Projektauswahl
    summary = <>Privates Dokument · <b>getrennt abgelegt</b></>;
  } else if (k.multi) {
    ready = selMulti.length > 0;
    summary = ready ? (
      <>Lieferschein · <b>{selMulti.length} {selMulti.length === 1 ? "Projekt" : "Projekte"}</b></>
    ) : (
      <>Mindestens ein Projekt wählen</>
    );
  } else if (selSingle === NO_PROJECT) {
    ready = k.erlaubtKeinProjekt;
    summary = <>Rechnung · <b>ohne Projekt</b> (Betriebskosten)</>;
  } else if (selSingle) {
    const p = projektById.get(selSingle);
    ready = !!p;
    summary = p ? <>{k.label} · <b>{p.title}</b></> : <>Projekt wählen, um fortzufahren</>;
  } else {
    ready = false;
    summary = cat === "bewirtung" ? <>Projekt wählen (Pflicht bei Bewirtung)</> : <>Projekt wählen oder „ohne Projekt"</>;
  }

  function buildContext(): CaptureContext | null {
    const ctx: CaptureContext = {
      ausgabe_typ: "lieferant", projekt_id: "", projekt_title: "",
      is_bewirtung: false, helfer_id: "", helfer_name: "",
    };
    if (IST_PRIVAT(cat)) {
      ctx.ausgabe_typ = "";
      ctx.privat = true;
      return ctx;
    }
    if (cat === "lieferschein") {
      if (selMulti.length === 0) return null;
      const projekte = selMulti.map((id) => projektById.get(id)).filter(Boolean) as ProjektShort[];
      ctx.ausgabe_typ = "";
      ctx.beleg_typ = "lieferschein";
      ctx.projekt_ids = projekte.map((p) => p.id);
      ctx.projekt_titles = projekte.map((p) => p.title);
      if (projekte.length === 1) {
        ctx.projekt_id = projekte[0].id;
        ctx.projekt_title = projekte[0].title;
      }
      return ctx;
    }
    if (selSingle === NO_PROJECT) {
      if (!k.erlaubtKeinProjekt) return null;
      ctx.ausgabe_typ = "betrieb";
      return ctx;
    }
    const p = selSingle ? projektById.get(selSingle) : undefined;
    if (!p) return null;
    ctx.projekt_id = p.id;
    ctx.projekt_title = p.title;
    if (cat === "bewirtung") {
      ctx.ausgabe_typ = "verpflegung";
      ctx.is_bewirtung = true;
    } else {
      ctx.ausgabe_typ = "lieferant";
    }
    return ctx;
  }

  function sichern() {
    const ctx = buildContext();
    if (ctx) onSichern(ctx, { cat, single: selSingle, multi: selMulti });
  }

  return (
    <div className="scan">
      <div className="scan-stage">
        <header className="scan-head">
          <button className="scan-back" onClick={onZurueck} aria-label="Zurück zu den Seiten"><SIcon name="back" /></button>
          <div className="scan-head-tt">
            <h1 className="scan-h1">Was ist das?</h1>
          </div>
          <div className="scan-head-bild" title={umfang}>
            {vorschauUrl ? <img src={vorschauUrl} alt="" /> : <span>PDF</span>}
            <em>{umfang}</em>
          </div>
        </header>

        <div className="scan-body">
          {/* Kategorie-Auswahl */}
          <div className="scan-chips" style={{ marginTop: 2 }}>
            {(Object.keys(KATEGORIEN) as Cat[]).map((key) => (
              <button
                key={key}
                className={"scan-chip scan-chip-cat" + (cat === key ? " is-sel" : "")}
                onClick={() => chooseCat(key)}
              >
                <span className="scan-chip-ic"><SIcon name={KATEGORIEN[key].icon} /></span>
                {KATEGORIEN[key].label}
              </button>
            ))}
          </div>

          {/* Projektauswahl — entfällt bei „Privat" */}
          {IST_PRIVAT(cat) ? (
            <div className="scan-note" style={{ marginTop: 16 }}>
              <span className="scan-note-ic"><SIcon name="lock" /></span>
              <span className="scan-note-tt">
                <b>Privates Dokument.</b> Wird getrennt von den Firmenbelegen abgelegt und läuft nicht
                in die DATEV-/Buchungs-Pipeline. Nach der Aufnahme kannst du Titel, Tags und
                (bei Garantie) das Ablaufdatum ergänzen.
              </span>
            </div>
          ) : (
            <ProjektAuswahl
              cat={cat}
              k={k}
              q={q}
              setQ={setQ}
              selSingle={selSingle}
              setSelSingle={setSelSingle}
              selMulti={selMulti}
              setSelMulti={setSelMulti}
              heuteProjekte={heuteQ.data ?? []}
              alleProjekte={aktiveQ.data ?? []}
              loading={heuteQ.isLoading || aktiveQ.isLoading}
            />
          )}
        </div>
      </div>

      <div className="scan-foot">
        <div className="scan-summary">{summary}</div>
        <button className="scan-cta" disabled={!ready} onClick={sichern}>
          <SIcon name="check" /> Sichern
        </button>
      </div>
    </div>
  );
}

// ───────────────────────── Projektauswahl ─────────────────────────
function ProjektAuswahl({
  cat, k, q, setQ, selSingle, setSelSingle, selMulti, setSelMulti,
  heuteProjekte, alleProjekte, loading,
}: {
  cat: Cat;
  k: (typeof KATEGORIEN)[Cat];
  q: string;
  setQ: (v: string) => void;
  selSingle: string | null;
  setSelSingle: (v: string | null) => void;
  selMulti: string[];
  setSelMulti: (v: string[]) => void;
  heuteProjekte: ProjektShort[];
  alleProjekte: ProjektShort[];
  loading: boolean;
}) {
  const ql = q.trim().toLowerCase();
  const heute = heuteProjekte.slice(0, HEUTE_COUNT);
  const matched = ql
    ? alleProjekte.filter((p) => `${p.title} ${p.kunde_name} ${p.ort}`.toLowerCase().includes(ql))
    : heute;

  const isSel = (id: string) => (k.multi ? selMulti.includes(id) : selSingle === id);
  const toggle = (id: string) => {
    if (k.multi) {
      setSelMulti(selMulti.includes(id) ? selMulti.filter((x) => x !== id) : [...selMulti, id]);
    } else {
      setSelSingle(selSingle === id ? null : id);
    }
  };

  return (
    <>
      <div className="scan-label">{cat === "lieferschein" ? "Für welche Projekte?" : "Welchem Projekt zuordnen?"}
        {k.multi && selMulti.length > 0 && <span className="n">{selMulti.length} gewählt</span>}
      </div>

      <div style={{ marginBottom: 10 }}>
        <SearchBox value={q} onChange={setQ} placeholder="Projektname, Kunde oder Ort tippen…" />
      </div>

      {/* „kein Projekt" nur wo erlaubt und nur ohne aktive Suche */}
      {k.erlaubtKeinProjekt && !ql && (
        <button
          className={"scan-opt" + (selSingle === NO_PROJECT ? " is-sel" : "")}
          onClick={() => setSelSingle(selSingle === NO_PROJECT ? null : NO_PROJECT)}
          style={{ marginBottom: 4 }}
        >
          <span className="scan-opt-ic"><SIcon name="ban" /></span>
          <span className="scan-opt-tt">
            <span className="scan-opt-name">Ohne Projekt</span>
            <span className="scan-opt-sub">Betriebskosten</span>
          </span>
          <span className="scan-tick"><SIcon name="check" /></span>
        </button>
      )}

      <div className="scan-label" style={{ paddingTop: 12 }}>{ql ? "Suchergebnisse" : "Heute auf der Baustelle"}</div>
      <div>
        {loading && <div className="scan-emptyq">Lade Projekte…</div>}
        {matched.map((p) => (
          <button key={p.id} className={"scan-opt" + (isSel(p.id) ? " is-sel" : "")} onClick={() => toggle(p.id)}>
            <span className="scan-opt-ic"><SIcon name="building" /></span>
            <span className="scan-opt-tt">
              <span className="scan-opt-name">{p.title || "(ohne Titel)"}</span>
              {(p.kunde_name || istAbgeschlossen(p)) && (
                <span className="scan-opt-sub">
                  {p.kunde_name}
                  {istAbgeschlossen(p) && `${p.kunde_name ? " · " : ""}abgeschlossen`}
                </span>
              )}
            </span>
            <span className={"scan-tick" + (k.multi ? " sq" : "")}><SIcon name="check" /></span>
          </button>
        ))}
        {!loading && !ql && heute.length === 0 && (
          <div className="scan-emptyq">Heute kein Projekt eingeplant.<br />Oben nach Projektname, Kunde oder Ort suchen.</div>
        )}
        {ql && matched.length === 0 && (
          <div className="scan-emptyq">Kein Projekt gefunden für „{q}“.<br />Tipp: nach Kunde oder Ort suchen.</div>
        )}
      </div>
    </>
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="scan-search">
      <SIcon name="search" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
      {value && (
        <button className="scan-search-x" onClick={() => onChange("")} aria-label="Leeren"><SIcon name="x" /></button>
      )}
    </div>
  );
}
